import type { Converse, ConverseMessage } from '@/entities/converse';
import { apiClient } from '@/shared/api/api-client';
import type { ApiResponse, PaginatedResponse } from '@/shared/api/types';
import { getStorageUrl } from '@/shared/lib';

// The API serialises conversations/messages in snake_case; map them to the
// camelCase domain types the UI consumes.
// biome-ignore lint/suspicious/noExplicitAny: raw API payload shape
function mapConverse(c: any): Converse {
  const avatar = c.participant_avatar ?? c.participantAvatar;
  return {
    id: c.converse_id ?? c.id,
    converseId: c.converse_id ?? c.id,
    title: c.title ?? '',
    content: c.content ?? '',
    type: c.type ?? 1,
    state: c.state ?? 1,
    createdAt: c.created_at ?? c.createdAt ?? '',
    lastMessage: c.content ?? undefined,
    lastMessageAt: c.updated_at ?? c.created_at ?? undefined,
    unreadCount: c.unread_count ?? c.unreadCount ?? 0,
    participantNodeId: c.participant_node_id ?? c.participantNodeId,
    participantName: c.participant_name ?? c.participantName,
    participantAvatar: avatar ? getStorageUrl(avatar) : undefined,
    participantOnline: c.participant_online ?? c.participantOnline ?? false,
  };
}

// biome-ignore lint/suspicious/noExplicitAny: raw API payload shape
function mapMessage(m: any): ConverseMessage {
  const sender = m.fk_node_id ?? m.fkNodeId ?? m.sender_node_id ?? m.senderNodeId;
  return {
    id: m.converse_message_id ?? m.id,
    converseMessageId: m.converse_message_id ?? m.converseMessageId ?? m.id,
    converseId: m.fk_converse_id ?? m.converse_id ?? m.converseId,
    content: m.content ?? '',
    title: m.title,
    type: m.type ?? 1,
    state: m.state ?? 1,
    fkNodeId: sender,
    senderNodeId: sender,
    createdAt: m.created_at ?? m.createdAt ?? '',
  };
}

export const converseApi = {
  getConverses: async (page = 1, count = 10): Promise<PaginatedResponse<Converse>> => {
    const response = await apiClient.get<ApiResponse<{ converses: unknown[]; total: number }>>(
      '/v1/converse',
      { params: { page, count } },
    );
    return {
      items: (response.data.data.converses ?? []).map(mapConverse),
      total: response.data.data.total,
      page,
      count,
    };
  },

  getConverse: async (converseId: number): Promise<Converse> => {
    const response = await apiClient.get<ApiResponse<unknown>>(`/v1/converse/${converseId}`);
    return mapConverse(response.data.data);
  },

  createConverse: async (
    participantNodeId: number,
    title?: string,
    content?: string,
  ): Promise<Converse> => {
    // API contract: { message, readers: number[] }, response wrapped in data.converse.
    const response = await apiClient.post<ApiResponse<{ converse: unknown }>>('/v1/converse', {
      message: content ?? title ?? 'Bonjour',
      readers: [participantNodeId],
    });
    return mapConverse(response.data.data.converse);
  },

  getMessages: async (
    converseId: number,
    page = 1,
    count = 50,
  ): Promise<PaginatedResponse<ConverseMessage>> => {
    const response = await apiClient.get<
      ApiResponse<{ converse_messages: unknown[]; total: number }>
    >(`/v1/converse/${converseId}/message`, { params: { page, count } });
    return {
      items: (response.data.data.converse_messages ?? []).map(mapMessage),
      total: response.data.data.total,
      page,
      count,
    };
  },

  sendMessage: async (converseId: number, content: string): Promise<ConverseMessage> => {
    // API contract: body field is `message`; created message wrapped in data.converse_message.
    const response = await apiClient.post<ApiResponse<{ converse_message: unknown }>>(
      `/v1/converse/${converseId}/message`,
      { message: content },
    );
    const created = mapMessage(response.data.data.converse_message ?? response.data.data);
    // API may not echo the content — fall back to what we sent.
    return created.content ? created : { ...created, content };
  },

  deleteConverse: async (converseId: number): Promise<void> => {
    await apiClient.delete(`/v1/converse/${converseId}`);
  },

  deleteMessage: async (converseId: number, messageId: number): Promise<void> => {
    await apiClient.delete(`/v1/converse/${converseId}/message/${messageId}`);
  },
};
